import React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { Grid, Typography } from '@mui/material';
import EmployeeCalendar from '../EmployeeCalendar';
import { ShiftCardWithTooltip } from '../ShiftCardWithTooltip';
import { RequestTypes, ShiftSegmentTypeName } from '../../../Enums';
import { MomentFormats } from '../../../utils';
import { getAsLongDate } from '../../../../common-client2/src/services/ukgDateTime';
import { generateSchedule } from './ManageRequests.helper';

const ShiftListFallback = ({ items, getShift, classes }) => (
    <Grid container style={{ backgroundColor: '#e4eaf0', padding: 8 }} spacing={1}>
        {items.map((item, idx) => {
            const shift = getShift(item);
            const firstValidSegment = shift.segments?.find(s => s.type !== ShiftSegmentTypeName.BREAK_SEGMENT);
            return (
                <React.Fragment key={`shift-fallback-${idx}`}>
                    <Grid item xs={3}>
                        <Typography>{getAsLongDate(shift.startDateTime)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <ShiftCardWithTooltip shift={shift} actionType={RequestTypes.voluntaryTimeOff} />
                    </Grid>
                    <Grid item xs={3}>
                        <Typography className={classes.employeeNameText}>
                            {firstValidSegment ? firstValidSegment.orgJobRef.qualifier : ''}
                        </Typography>
                    </Grid>
                </React.Fragment>
            );
        })}
    </Grid>
);

const RequestDetailPanel = ({ rowData, classes }) => {
    switch (rowData.actionType) {
        case RequestTypes.toCover:
            return (
                <Grid container className={clsx(classes.detailPanel, 'more-detail')}>
                    <Grid item xs={1} className={classes.detailPanelShift}>
                        <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.employeeFrom.name}</Typography>
                    </Grid>
                    <Grid item xs={11} className="manager-dashboard">
                        <EmployeeCalendar schedule={generateSchedule(rowData, 'from')} shiftDate={rowData.shift.startDateTime} />
                    </Grid>
                    <Grid item xs={12} className={classes.divider} />
                    <Grid item xs={1} className={classes.detailPanelShift}>
                        <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.employeeTo.name}</Typography>
                    </Grid>
                    <Grid item xs={11} className="manager-dashboard">
                        <EmployeeCalendar schedule={generateSchedule(rowData, 'to')} shiftDate={rowData.shift.startDateTime} />
                    </Grid>
                </Grid>
            );
        case RequestTypes.shiftSwap:
            if (rowData.swapRequestShiftInfo.length <= 1) {
                return (
                    <Grid container className={clsx(classes.detailPanel, 'more-detail')}>
                        <Grid item xs={1} className={classes.detailPanelShift}>
                            <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.offerorEmployeeName}</Typography>
                        </Grid>
                        <Grid item xs={11} className="manager-dashboard">
                            <EmployeeCalendar schedule={generateSchedule(rowData, 'from')} shiftDate={rowData.swapRequestShiftInfo[0].shift.startDateTime} />
                        </Grid>
                        <Grid item xs={12} className={classes.divider} />
                        <Grid item xs={1} className={classes.detailPanelShift}>
                            <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.swapProposal.proposalEmployeeName}</Typography>
                        </Grid>
                        <Grid item xs={11} className="manager-dashboard">
                            <EmployeeCalendar schedule={generateSchedule(rowData, 'to')} shiftDate={rowData.swapProposalShiftInfo[0].shift.startDateTime} />
                        </Grid>
                    </Grid>
                );
            }
            return (
                <div className={classes.detailPanel}>
                    <Typography component="h2" className={classes.employeeNameDetail}>{rowData.offerorEmployeeName}</Typography>
                    <ShiftListFallback items={rowData.swapRequestShiftInfo} getShift={i => i.shift} classes={classes} />
                    <div className={classes.divider} />
                    <Typography component="h2" className={classes.employeeNameDetail}>{rowData.swapProposal.proposalEmployeeName}</Typography>
                    <ShiftListFallback items={rowData.swapProposalShiftInfo} getShift={i => i.shift} classes={classes} />
                </div>
            );
        case RequestTypes.openShift:
            return (
                <Grid container className={clsx(classes.detailPanel, 'more-detail')}>
                    <Grid item xs={1} className={classes.detailPanelShift}>
                        <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.employeeTo.name}</Typography>
                    </Grid>
                    <Grid item xs={11} className="manager-dashboard">
                        <EmployeeCalendar schedule={generateSchedule(rowData, 'to')} shiftDate={rowData.shift.startDateTime} />
                    </Grid>
                </Grid>
            );
        case RequestTypes.voluntaryTimeOff: {
            const schedules = generateSchedule(rowData, 'to');
            const eeScheduleStartDates = schedules.map(d => moment(d.start));
            const eeScheduleEndDates = schedules.map(d => moment(d.end));
            const startOfWeekDate = moment.min(eeScheduleStartDates).utc().clone().day(0);
            const endOfWeekDate = moment.max(eeScheduleEndDates).utc().clone().day(6);
            const weeksCount = Math.ceil(endOfWeekDate.diff(startOfWeekDate, 'weeks', true));
            const parsedShifts = (rowData.shifts || []).map(x => moment(x.startDateLocationTime));
            const firstAffectedShift = moment.min(parsedShifts);

            if (weeksCount <= 6) {
                return (
                    <Grid container className={clsx(classes.detailPanel, 'more-detail')}>
                        <Grid item xs={12} className={classes.detailPanelShift}>
                            <Typography component="h2" className={classes.employeeNameDetail} align="center">
                                {startOfWeekDate.format(MomentFormats.VERBOSE_LONG_DATE_WITH_DAY)} to {endOfWeekDate.format(MomentFormats.VERBOSE_LONG_DATE_WITH_DAY)}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} className={classes.detailPanelShift}>
                            <Typography component="h2" className={classes.employeeNameDetail} align="center">{rowData.employeeTo.name}</Typography>
                        </Grid>
                        <Grid item xs={11} className="manager-dashboard">
                            <EmployeeCalendar schedule={schedules} shiftDate={firstAffectedShift} weeksCount={weeksCount} />
                        </Grid>
                    </Grid>
                );
            }
            const sortedShifts = [...(rowData.shifts || [])].sort((a, b) =>
                a.startDateLocationTime < b.startDateLocationTime ? -1 : 1);
            return (
                <div className={classes.detailPanel}>
                    <Typography component="h2" className={classes.employeeNameDetail} align="center">
                        {startOfWeekDate.format(MomentFormats.VERBOSE_LONG_DATE_WITH_DAY)} to {endOfWeekDate.format(MomentFormats.VERBOSE_LONG_DATE_WITH_DAY)}
                    </Typography>
                    <ShiftListFallback items={sortedShifts} getShift={i => i.shift} classes={classes} />
                </div>
            );
        }
        default:
            return null;
    }
};

export default RequestDetailPanel;
